import ReactSelect from 'components/common/react-select/ReactSelect';
import React from 'react';
import { IFilterColumn } from 'components/report-content/components/manage-filters/index';
import { Box } from '@mui/material';
import useBundleTranslation from 'i18n';

const VIRTUAL_LIST_MIN_OPTIONS = 200;

// Option value of the synthetic "All" entry inside the select only. "All" is stored as no selection ([]),
// while '' is a real blank dataset value.
export const ALL_OPTION_VALUE = '__mi_filter_all__';

// With addEmptyOption=false (Reference Manager) "All" keeps the '' value its own "All Values" entry uses.
export function prepareSelectFilterData(filter: IFilterColumn, addEmptyOption: boolean = true) {
    if (!Array.isArray(filter.data)) {
        return { data: [], values: [] };
    }
    const decodeHtml = (str: string): string => {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
    };

    // NULL and '' are the same blank value; keep a single '' option for both.
    let data = filter.data
        .map((el: any) => {
            const raw =
                'undefined' !== typeof el[filter.uid + '-formatted'] ? el[filter.uid + '-formatted'] : el[filter.uid];
            return {
                value: el[filter.uid] ?? '',
                label: typeof raw === 'string' ? decodeHtml(raw) : raw,
            };
        })
        .filter((el: { value: any }, i: number, list: Array<{ value: any }>) =>
            el.value !== '' ? true : list.findIndex((other) => other.value === '') === i,
        );

    let values = filter.savedValues;
    if (
        filter.savedValues &&
        filter.savedValues.length > 0 &&
        filter?.use_external_filter_value == 'Y' &&
        filter?.external_delimiter &&
        filter?.external_delimiter > ''
    ) {
        for (let i = 0; i < values.length; i++) {
            let cmp = values[i].trim().toLowerCase();
            for (let j = 0; j < data.length; j++) {
                if (data[j].label.toLowerCase() == cmp) {
                    values[i] = data[j].value;
                }
            }
        }
    }

    if (filter.filterType == 'dropdown') {
        const allValue = addEmptyOption ? ALL_OPTION_VALUE : '';
        if (!addEmptyOption && filter.include_all_value != 'N') {
            data = data.filter((el) => el.value !== '');
        }
        if (filter.override_all_value == 'Y') {
            // The dataset's own "All"-type row already exists in `data` under its own label
            // (e.g. "Germany"); treat it as the synthetic "All" entry, so selecting it clears the selection.
            const allValueRow = data.find((el) => el.value == filter.override_all_value_label);
            if (allValueRow) {
                allValueRow.value = allValue;
            }
            if (filter.savedValues.length == 0) {
                values = [allValue];
            }
        } else if (filter.include_all_value == 'Y') {
            if (addEmptyOption) {
                data.unshift({ value: allValue, label: 'All' });
            }
            if (filter.savedValues.length == 0) {
                values = [allValue];
            }
        } else {
            if (filter.savedValues.length == 0 && data.length) {
                values = [data[0].value];
            }
        }
    }
    return { data, values };
}

export default function FilterControlSelect({
    filter,
    onChange,
}: {
    filter: IFilterColumn;
    onChange: (newValue: any) => void;
}) {
    const { t } = useBundleTranslation(['components/report-content']);
    // Prepare Data object for Select2
    const { data, values } = prepareSelectFilterData(filter);
    if (
        filter.filterType == 'dropdown' &&
        filter.include_all_value != 'Y' &&
        filter.override_all_value != 'Y' &&
        filter.savedValues.length == 0 &&
        data.length
    ) {
        onChange(data[0].value);
    }

    const options = data.map((el) =>
        el.value === '' && !el.label ? { ...el, label: t('manage_filters.blank_value') } : el,
    );

    // The clear button reports a single select's value as '', which is the blank value's own value.
    const handleUpdate = (value: any, newValue: any) => {
        if (filter.filterType == 'dropdown' && (newValue == null || value === ALL_OPTION_VALUE)) {
            onChange([]);
            return;
        }
        onChange(value);
    };

    return (
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <ReactSelect
                dataTest={`filter-${filter.column_name}`}
                placeholder={'All'}
                isMulti={filter.filterType == 'multiselect'}
                isMultiOneRowMode={filter.filterType == 'multiselect'}
                data={options}
                update={handleUpdate}
                value={values}
                applyVirtualList={options.length > VIRTUAL_LIST_MIN_OPTIONS}
            />
        </Box>
    );
}
